import type { ReferenceOption } from '../../form/forms';
import { resolveOptionIdentity } from './autocompleteOptionIdentityUtils';

export const makeGetChipKeys = (
    generatedChipKeys: Map<string, string>,
    makeNextGeneratedChipKey: () => string
) => {
    return (selectedOptions: ReferenceOption[]): string[] => {
        const duplicateCount = new Map<string, number>();

        return selectedOptions.map((option) => {
            const optionIdentity = resolveOptionIdentity(option);
            if (optionIdentity) {
                return `id:${optionIdentity}`;
            }

            const baseSignature = `label:${option?.label ?? ''}|subLabel:${option?.subLabel ?? ''}`;
            const nextOccurrence = (duplicateCount.get(baseSignature) ?? 0) + 1;
            duplicateCount.set(baseSignature, nextOccurrence);

            const signature = `${baseSignature}|occurrence:${nextOccurrence}`;
            const existingLocalKey = generatedChipKeys.get(signature);
            if (existingLocalKey) {
                return existingLocalKey;
            }

            const generatedKey = makeNextGeneratedChipKey();
            generatedChipKeys.set(signature, generatedKey);
            return generatedKey;
        });
    };
};

export const isReferenceOptionEqualById = (option: ReferenceOption, value: ReferenceOption): boolean => {
    const valueWithReference = value as ReferenceOption & { reference?: { id?: string } };
    const valueId = valueWithReference?.reference?.id ?? valueWithReference?.id;
    return option && valueId ? option.id === valueId : false;
};

type ReplaceEditedChipArgs = {
    multiple: boolean;
    editingChipKey: string | null;
    currentOptions: ReferenceOption[] | undefined;
    selectedOptions: ReferenceOption[];
    getChipKeys: (selectedOptions: ReferenceOption[]) => string[];
    isReferenceOptionEqualToValue: (option: ReferenceOption, value: ReferenceOption) => boolean;
};

export const replaceEditedChipSelection = ({
    multiple,
    editingChipKey,
    currentOptions,
    selectedOptions,
    getChipKeys,
    isReferenceOptionEqualToValue
}: ReplaceEditedChipArgs): ReferenceOption[] => {
    if (!multiple || !editingChipKey || !Array.isArray(currentOptions)) {
        return selectedOptions;
    }

    const currentChipKeys = getChipKeys(currentOptions);
    const editingIndex = currentChipKeys.indexOf(editingChipKey);

    if (editingIndex < 0) {
        return selectedOptions;
    }

    const editedOption = currentOptions[editingIndex];
    const currentWithoutEdited = currentOptions.filter((_, index) => index !== editingIndex);
    const isEditedOptionStillPresent = selectedOptions.some((option) =>
        isReferenceOptionEqualToValue(option, editedOption)
    );

    const addedOption = selectedOptions.find(
        (option) => !currentWithoutEdited.some((currentOption) => isReferenceOptionEqualToValue(currentOption, option))
    );

    if (!addedOption || isEditedOptionStillPresent) {
        return selectedOptions;
    }

    const replacedOptions = [...currentWithoutEdited];
    replacedOptions.splice(editingIndex, 0, addedOption);
    return replacedOptions;
};

type ReplaceEditedChipWithOptionArgs = {
    multiple: boolean;
    editingChipKey: string | null;
    currentOptions: ReferenceOption[] | undefined;
    selectedOption: ReferenceOption;
    getChipKeys: (selectedOptions: ReferenceOption[]) => string[];
};

export const replaceEditedChipWithOptionSelection = ({
    multiple,
    editingChipKey,
    currentOptions,
    selectedOption,
    getChipKeys
}: ReplaceEditedChipWithOptionArgs): ReferenceOption[] => {
    if (!multiple || !editingChipKey || !Array.isArray(currentOptions)) {
        return Array.isArray(currentOptions) ? [...currentOptions] : [selectedOption];
    }

    const currentChipKeys = getChipKeys(currentOptions);
    const editingIndex = currentChipKeys.indexOf(editingChipKey);

    if (editingIndex < 0) {
        return [...currentOptions, selectedOption];
    }

    const replacedOptions = [...currentOptions];
    replacedOptions.splice(editingIndex, 1, selectedOption);
    return replacedOptions;
};

export const syncUnresolvedChipKeysSelection = (
    selectedOptions: ReferenceOption[],
    previousUnresolvedChipKeys: string[],
    getChipKeys: (selectedOptions: ReferenceOption[]) => string[]
): string[] => {
    const nextChipKeys = getChipKeys(selectedOptions);
    return previousUnresolvedChipKeys.filter((key) => nextChipKeys.includes(key));
};
